import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import {
  fetchStates,
  createState,
  updateState,
  deleteState,
  resetSuccess,
} from '../../store/slices/statesSlice';
import { DataTable, Modal, ConfirmDialog, SearchBox } from '../../components/Common';
import { getStatusBadgeClass, getStatusText } from '../../utils/helpers';

const States = () => {
  const dispatch = useDispatch();
  const { states, isLoading, success, error } = useSelector((state) => state.states);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', status: true });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    dispatch(fetchStates());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(selectedItem ? 'Updated successfully!' : 'Created successfully!');
      dispatch(resetSuccess());
      closeModal();
    }
  }, [success, dispatch, selectedItem]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const openModal = (item = null) => {
    if (item) {
      setSelectedItem(item);
      setFormData({ name: item.name, status: item.status });
    } else {
      setSelectedItem(null);
      setFormData({ name: '', status: true });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    setFormData({ name: '', status: true });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Please enter a state name');
      return;
    }

    if (selectedItem) {
      dispatch(updateState({ id: selectedItem._id, data: formData }));
    } else {
      dispatch(createState(formData));
    }
  };

  const handleDelete = () => {
    if (selectedItem) {
      dispatch(deleteState(selectedItem._id)).then((result) => {
        if (deleteState.fulfilled.match(result)) {
          toast.success('Deleted successfully!');
          setIsDeleteOpen(false);
          setSelectedItem(null);
        }
      });
    }
  };

  const filteredStates = states.filter((state) =>
    state.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    {
      header: 'State Name',
      accessor: 'name',
    },
    {
      header: 'Status',
      render: (row) => (
        <span className={`badge ${getStatusBadgeClass(row.status)}`}>
          {getStatusText(row.status)}
        </span>
      ),
    },
    {
      header: 'Actions',
      render: (row) => (
        <div className="table-actions">
          <button
            className="btn btn-secondary btn-icon"
            onClick={() => openModal(row)}
            title="Edit"
          >
            <FiEdit2 />
          </button>
          <button
            className="btn btn-danger btn-icon"
            onClick={() => {
              setSelectedItem(row);
              setIsDeleteOpen(true);
            }}
            title="Delete"
          >
            <FiTrash2 />
          </button>
        </div>
      ),
      style: { width: '120px' },
    },
  ];

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <h3 className="card-title">States</h3>
            <SearchBox
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search states..."
            />
          </div>
          <button className="btn btn-primary" onClick={() => openModal()}>
            <FiPlus /> Add State
          </button>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <DataTable
            columns={columns}
            data={filteredStates}
            isLoading={isLoading}
            emptyMessage="No states found"
          />
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={selectedItem ? 'Edit State' : 'Add State'}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">State Name *</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter state name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Status</label>
            <select
              className="form-control form-select"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value === 'true' })}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div className="modal-footer" style={{ padding: '16px 0 0', borderTop: 'none' }}>
            <button type="button" className="btn btn-secondary" onClick={closeModal}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : selectedItem ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Delete State"
        message={`Are you sure you want to delete "${selectedItem?.name}"? This action cannot be undone.`}
        isLoading={isLoading}
      />
    </div>
  );
};

export default States;
